import React, { useEffect, useRef, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import Highcharts from 'highcharts/highstock'
import HighchartsReact from 'highcharts-react-official'
import { TrafficResponseWrapper } from 'common/types/kraken-core/TrafficVolume'
import { MESSAGE_STATUS_COLORS } from 'common/constants'

export interface MessageTrafficVolumeChartProps {
  traffic?: TrafficResponseWrapper
}

export default function MessageTrafficVolumeChart(props: MessageTrafficVolumeChartProps) {
  const theme = useTheme()
  const [series, setSeries] = useState<any>()
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null) // Add ref for HighchartsReact

  useEffect(() => {
    if (!props.traffic) {
      return
    }

    chartComponentRef.current?.chart.showLoading()
    const series = new Map<string, Array<Array<any>>>()
    const summary: any = {} // Initialize summary object

    const contextTraffic = props.traffic
    contextTraffic.traffic.buckets.forEach(bucket => {
      Object.keys(bucket.per_agg).forEach(key => {
        const timestamp = new Date(bucket.datetime).getTime() // Convert to timestamp
        if (!series.has(key)) {
          series.set(key, [[timestamp, bucket.per_agg[key]]])
        } else {
          const serie = series.get(key) as any
          series.set(key, [...serie, [timestamp, bucket.per_agg[key]]])
        }

        // Update summary
        if (!summary[key]) {
          summary[key] = bucket.per_agg[key]
        } else {
          summary[key] += bucket.per_agg[key]
        }
      })
    })

    const chartSeries: any = []
    series.forEach((value, key) => {
      const color = MESSAGE_STATUS_COLORS[String(key)] || '#2b908f'

      chartSeries.push({
        name: key,
        data: value,
        color,
      })
    })

    setSeries(chartSeries)

    // Reset chart zoom
    const chart = chartComponentRef.current?.chart
    if (chart) {
      chart.xAxis[0].setExtremes()
    }
    chartComponentRef.current?.chart.hideLoading()
  }, [props.traffic])

  useEffect(() => {
    const chart = chartComponentRef.current?.chart
    if (props.traffic?.isLoading) {
      chart?.showLoading() // Show loading indicator
    } else {
      chart?.hideLoading() // Hide loading indicator
    }
  }, [props.traffic?.isLoading])

  const options = {
    chart: {
      type: 'column',
      style: {
        fontFamily: 'Arial, sans-serif', // Set default font
      },
      animation: false, // Disable animations
      height: 300,
    },
    navigator: {
      enabled: true,
    },
    rangeSelector: {
      enabled: false,
    },
    time: {
      useUTC: false, // Use local time instead of UTC
      timezone: undefined, // Let the browser decide the timezone
    },
    title: {
      align: 'left',
    },
    xAxis: {
      // type: 'datetime', // Use datetime axis
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Values',
      },
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: false, // Ensure data labels are not shown
        },
        dataGrouping: {
          enabled: true,
          approximation: 'sum',
          groupPixelWidth: 20,
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: series,
  }

  return (
    <HighchartsReact
      ref={chartComponentRef} // Attach ref to HighchartsReact component
      constructorType={'stockChart'}
      highcharts={Highcharts}
      options={options}
    />
  )
}
